import { useEffect, useMemo, useState } from "react";
import { getIncidents } from "../../api/incidents";
import { useNavigate } from "react-router-dom";
import { getLang } from "../../store/uiStore";
import { getUser } from "../../store/authStore";
import "./IncidentsList.css";

export default function IncidentsList() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const navigate = useNavigate();
  const lang = getLang();
  const user = getUser();
  const isInspector = ["regional_inspector", "clinic_inspector", "central"].includes(user?.role);

  const tr = lang === "ru" ? {
    title: "Экстренные извещения",
    subtitle: "Регистрация и просмотр экстренных извещений.",
    create: "Создать новое извещение",
    infection: "Опасные инфекционные заболевания",
    medicalError: "Медицинская ошибка",
    adverseEvent: "Нежелательное явление",
    clinic: "Клиника",
    disease: "Название болезни",
    diagnosisDate: "Дата диагноза",
    errorType: "Вид ошибки",
    reactionCategory: "Категория реакции",
    suspectedDrug: "Предполагаемый препарат",
    sentDate: "Дата отправки",
    search: "Поиск по клинике или полям извещения",
    empty: "Извещений пока нет",
    noResults: "По запросу ничего не найдено",
    noClinic: "Клиника не указана",
    noValue: "—",
    loading: "Загрузка извещений…",
    error: "Не удалось загрузить извещения. Попробуйте обновить страницу.",
  } : {
    title: "Хабарномаҳои фаврӣ",
    subtitle: "Сабт ва тамошои хабарномаҳои фаврӣ.",
    create: "Сохтани хабарномаи нав",
    infection: "Бемории хавфноки сироятӣ",
    medicalError: "Хатои тиббӣ",
    adverseEvent: "Ҳодисаи номатлуб",
    clinic: "Клиника",
    disease: "Номи беморӣ",
    diagnosisDate: "Санаи ташхис",
    errorType: "Навъи хато",
    reactionCategory: "Категорияи реаксия",
    suspectedDrug: "Доруи гумонбар",
    sentDate: "Санаи фиристодан",
    search: "Ҷустуҷӯ аз рӯи клиника ё майдонҳои хабарнома",
    empty: "Ҳоло хабарномаҳо вуҷуд надоранд",
    noResults: "Аз рӯи дархост натиҷа ёфт нашуд",
    noClinic: "Клиника нишон дода нашудааст",
    noValue: "—",
    loading: "Хабарномаҳо бор шуда истодаанд…",
    error: "Хабарномаҳо бор карда нашуданд. Саҳифаро нав кунед.",
  };

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true);
        setLoadError(false);
        const response = await getIncidents();
        const items = Array.isArray(response.data) ? response.data : (response.data?.results || []);
        if (active) setData([...items].sort((a, b) => new Date(b.sent_at || b.created_at || 0) - new Date(a.sent_at || a.created_at || 0)));
      } catch (error) {
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return tr.noValue;
    if (Array.isArray(value)) return value.map(displayValue).join(", ");
    if (typeof value === "object") return tr.noValue;
    const diagnosisTranslations = {
      "Тоун": "Чума",
      "Вабо": "Холера",
      "Оспаи табиӣ": "Натуральная оспа",
      "Бемории шадиди фалаҷи суст, аз ҷумла ҳар як ҳодисаи бо ваксина алоқаманд": "Острый вялый паралич (ОВП), включая каждый случай, связанный с вакцинацией",
      "Зукоми аз вируси дорои хусусияти баланди касалиовар (H5N1 ва ғайра) пайдошуда ва инчунин вируси антигении намуди нав": "Грипп, вызванный высокопатогенным вирусом (H5N1 и др.), а также вирусом нового антигенного типа",
      "Бемории шадиди роҳҳои нафас (ТОРС)": "Тяжёлый острый респираторный синдром (ТОРС / SARS)",
      "Табби зард": "Жёлтая лихорадка",
      "Табби Лас, Марбург": "Лихорадка Ласса, болезнь, вызванная вирусом Марбург",
      "Табби Нили ғарбӣ, Нил, водии Риф": "Лихорадка Западного Нила, Нила и лихорадка долины Рифт",
      "Табби хунини Қрим": "Крымская геморрагическая лихорадка",
      "Вараҷа": "Малярия",
      "Ҳорӣ": "Бешенство",
      "Сӯхтанӣ": "Сибирская язва",
      "Сирояти ВНМО": "ВИЧ-инфекция",
      "Ҳасба (доманаи доначадори эпидемикӣ), доманаи бозгашта (бемории Брилл)": "Сыпной тиф (эпидемический), рецидивный сыпной тиф (болезнь Брилля)",
      "Доманаи шабушии барангезандааш Rickettsia prowazekii": "Сыпной тиф, вызванный Rickettsia prowazekii, передающийся через платяных вшей",
      "Легионеллёз": "Легионеллёз",
    };
    if (lang === "ru" && diagnosisTranslations[String(value)]) return diagnosisTranslations[String(value)];
    const translations = {
      preliminary: lang === "ru" ? "Предварительный" : "Пешакӣ",
      final: lang === "ru" ? "Окончательный" : "Ниҳоӣ",
      mild: lang === "ru" ? "Лёгкая" : "Сабук",
      moderate: lang === "ru" ? "Умеренная" : "Миёна",
      severe: lang === "ru" ? "Тяжёлая" : "Вазнин",
    };
    return translations[String(value)] || String(value);
  };

  const formatDate = (value) => {
    if (!value) return tr.noValue;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return displayValue(value);
    return date.toLocaleDateString(lang === "ru" ? "ru-RU" : "tg-TJ");
  };

  const rows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const getText = (item, kind) => {
      const form = item.data || {};
      if (kind === "infection") return [form.disease, form.diagnosis_date];
      if (kind === "medical_error") return [form.error_type, form.error_type_other, form.description];
      return [form.reaction?.category, form.suspected_drug?.trade_name, form.suspected_drug?.generic_name];
    };
    return data.filter((item) => {
      if (!query) return true;
      const text = [item.clinic_name, item.clinic, item.type, ...getText(item, item.type)]
        .flat(Infinity).filter(Boolean).join(" ").toLocaleLowerCase();
      return text.includes(query);
    });
  }, [data, search]);

  const sections = [
    {
      key: "infection", title: tr.infection,
      columns: [
        { key: "clinic", label: tr.clinic, value: (item) => item.clinic_name || tr.noClinic },
        { key: "disease", label: tr.disease, value: (item) => displayValue(item.data?.disease || item.data?.diagnosis) },
        { key: "diagnosisDate", label: tr.diagnosisDate, value: (item) => formatDate(item.data?.diagnosis_date) },
        { key: "sentDate", label: tr.sentDate, value: (item) => formatDate(item.sent_at) },
      ],
    },
    {
      key: "medical_error", title: tr.medicalError,
      columns: [
        { key: "clinic", label: tr.clinic, value: (item) => item.clinic_name || tr.noClinic },
        { key: "errorType", label: tr.errorType, value: (item) => displayValue(item.data?.error_type) },
        { key: "sentDate", label: tr.sentDate, value: (item) => formatDate(item.sent_at) },
      ],
    },
    {
      key: "adverse_event", title: tr.adverseEvent,
      columns: [
        { key: "clinic", label: tr.clinic, value: (item) => item.clinic_name || tr.noClinic },
        { key: "reactionCategory", label: tr.reactionCategory, value: (item) => displayValue(item.data?.reaction?.category || item.data?.reaction_category) },
        { key: "suspectedDrug", label: tr.suspectedDrug, value: (item) => displayValue(item.data?.suspected_drug?.trade_name || item.data?.suspected_drug?.generic_name || item.data?.drug_name) },
        { key: "sentDate", label: tr.sentDate, value: (item) => formatDate(item.sent_at) },
      ],
    },
  ];

  return (
    <main className="nizom-incidents-page">
      <div className="nizom-incidents-container">
        <header className="nizom-incidents-header">
          <div>
            <div className="nizom-incidents-title-row"><span className="nizom-incidents-title-accent" /><h1>{tr.title}</h1></div>
            <p className="nizom-incidents-subtitle">{tr.subtitle}</p>
          </div>
          {!isInspector && <button type="button" className="nizom-incidents-create-button" onClick={() => navigate("/incidents/create")}><span className="nizom-incidents-plus" aria-hidden="true">+</span>{tr.create}</button>}
        </header>

        <label className="nizom-incidents-search">
          <span>{tr.search}</span>
          <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={tr.search} />
        </label>

        {loading ? <section className="nizom-incidents-empty"><p>{tr.loading}</p></section> : loadError ? <section className="nizom-incidents-empty"><p>{tr.error}</p></section> : data.length === 0 ? <section className="nizom-incidents-empty"><p>{tr.empty}</p></section> : null}

        {!loading && !loadError && sections.map((section) => {
          const sectionRows = rows.filter((item) => item.type === section.key);
          return (
            <section className="nizom-incident-section" key={section.key}>
              <div className="nizom-incident-section-heading"><h2>{section.title}</h2><span>{sectionRows.length}</span></div>
              {sectionRows.length === 0 ? <p className="nizom-incident-section-empty">{tr.noResults}</p> : (
                <div className="nizom-incidents-table-wrap">
                  <table className="nizom-incidents-table">
                    <thead><tr>{section.columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
                    <tbody>{sectionRows.map((item) => (
                      <tr key={item.id} onClick={() => navigate(`/incidents/${item.id}`)} className={item.viewed_by_inspector ? "" : "is-unread"} tabIndex={0} onKeyDown={(event) => { if (event.key === "Enter") navigate(`/incidents/${item.id}`); }}>
                        {section.columns.map((column) => <td key={column.key}>{column.value(item)}</td>)}
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </section>
          );
        })}
        {!loading && !loadError && data.length > 0 && rows.length === 0 && <section className="nizom-incidents-empty"><p>{tr.noResults}</p></section>}
      </div>
    </main>
  );
}
